import {
  setConversations,
  setMessages,
  addMessage,
  setLoading,
  setError,
  updateConversation,
  markAsRead,
  addTypingUser,
  removeTypingUser,
  setSearchResults,
  clearError,
  setCurrentConversation,
} from './chatSlice';
import firebaseChatService from '../services/firebaseChatService';
import { auth } from '../config/firebase';

// Thunk actions
export const fetchConversations = () => async dispatch => {
  try {
    dispatch(setLoading(true));
    dispatch(clearError());

    const conversations = await firebaseChatService.getConversations();
    dispatch(setConversations(conversations));
  } catch (error) {
    console.error('Error fetching conversations:', error);
    dispatch(setError(error.message));
  }
};

export const subscribeToConversations = () => dispatch => {
  return firebaseChatService.subscribeToConversations(conversations => {
    dispatch(setConversations(conversations));
  });
};

export const fetchMessages = conversationId => async dispatch => {
  try {
    dispatch(setLoading(true));
    dispatch(clearError());

    const messages = await firebaseChatService.getMessages(conversationId);
    dispatch(setMessages(messages));
  } catch (error) {
    console.error('Error fetching messages:', error);
    dispatch(setError(error.message));
  }
};

export const subscribeToMessages = conversationId => dispatch => {
  return firebaseChatService.subscribeToMessages(conversationId, messages => {
    dispatch(setMessages(messages));
  });
};

export const sendMessage = (
  conversationId,
  text,
  replyToText,
  replyToType,
  replyToImageUrl,
  replyToFileName,
) => async (dispatch, getState) => {
  try {
    dispatch(clearError());

    // Check if this is a temporary conversation
    if (conversationId.startsWith('temp_')) {
      const participantId = conversationId.replace('temp_', '');
      const state = getState();
      const currentConversation = state.chat.currentConversation;
      const participantName =
        currentConversation?.participantName ||
        currentConversation?.conversationName;

      const newConversation =
        await firebaseChatService.createConversation(
          [participantId],
          participantName,
        );

      dispatch(setCurrentConversation(newConversation));

      const newMessage = await firebaseChatService.sendMessage(
        newConversation.id,
        text,
        replyToText,
        replyToType,
        replyToImageUrl,
        replyToFileName,
      );
      dispatch(addMessage(newMessage));

      dispatch(
        updateConversation({
          conversationId: newConversation.id,
          updates: {
            lastMessage: text,
            lastMessageTime: newMessage.createdAt,
          },
        }),
      );

      dispatch(subscribeToMessages(newConversation.id));
      dispatch(fetchConversations());

      return newMessage;
    } else {
      const newMessage = await firebaseChatService.sendMessage(
        conversationId,
        text,
        replyToText,
        replyToType,
        replyToImageUrl,
        replyToFileName,
      );
      dispatch(addMessage(newMessage));

      const currentUser = auth.currentUser;

      dispatch(
        updateConversation({
          conversationId,
          updates: {
            lastMessage: text,
            lastMessageTime: newMessage.createdAt,
            [`unreadCount.${currentUser?.uid}`]: 0,
          },
        }),
      );

      return newMessage;
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Send image
export const sendImage = (conversationId, image) => async dispatch => {
  try {
    const newMessage =
      await firebaseChatService.sendImageMessage(conversationId, image);
    dispatch(addMessage(newMessage));
    dispatch(
      updateConversation({
        conversationId,
        updates: {
          lastMessage: '[Photo]',
          lastMessageTime: newMessage.createdAt,
        },
      }),
    );
    return newMessage;
  } catch (error) {
    console.error('Error sending image:', error);
    throw error;
  }
};

// Send generic file
export const sendFile = (conversationId, file) => async dispatch => {
  try {
    const newMessage =
      await firebaseChatService.sendFileMessage(conversationId, file);
    dispatch(addMessage(newMessage));
    dispatch(
      updateConversation({
        conversationId,
        updates: {
          lastMessage: `[File] ${newMessage.fileName}`,
          lastMessageTime: newMessage.createdAt,
        },
      }),
    );
    return newMessage;
  } catch (error) {
    console.error('Error sending file:', error);
    throw error;
  }
};

export const markConversationAsRead =
  conversationId => async (dispatch, getState) => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser?.uid) {
        console.error('No current user found');
        return;
      }

      await firebaseChatService.markConversationAsRead(conversationId);

      dispatch(markAsRead({ conversationId, userId: currentUser.uid }));
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

export const searchConversations = queryText => async dispatch => {
  try {
    dispatch(clearError());
    const results = await firebaseChatService.searchConversations(queryText);
    dispatch(setSearchResults(results));
  } catch (error) {
    console.error('Error searching conversations:', error);
    dispatch(setError(error.message));
  }
};

export const initializeChat = () => async dispatch => {
  try {
    await firebaseChatService.ensureUserDocument();
    await dispatch(fetchConversations());
    return dispatch(subscribeToConversations());
  } catch (error) {
    console.error('Error initializing chat:', error);
    dispatch(setError(error.message));
    return () => {};
  }
};

export const subscribeToUserStatus = (userId, callback) => dispatch => {
  return firebaseChatService.subscribeToUserStatus(userId, callback);
};

export const blockUser = blockedUserId => async dispatch => {
  try {
    await firebaseChatService.blockUser(blockedUserId);
  } catch (error) {
    console.error('Error blocking user:', error);
    throw error;
  }
};

export const unblockUser = blockedUserId => async dispatch => {
  try {
    await firebaseChatService.unblockUser(blockedUserId);
  } catch (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }
};

export const reportUser = ({ reportedUserId, reason }) => async dispatch => {
  try {
    await firebaseChatService.reportUser(
      reportedUserId,
      reason || 'Inappropriate behavior',
    );
  } catch (error) {
    console.error('Error reporting user:', error);
    throw error;
  }
};

export const deleteConversation = conversationId => async dispatch => {
  try {
    await firebaseChatService.deleteConversation(conversationId);
    dispatch(fetchConversations());
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

// Open or create conversation by user ID
export const openOrCreateConversationByUserId = (recipientId, recipientName) => async (dispatch, getState) => {
  try {
    if (!recipientId) throw new Error('recipientId required');
    
    // Ensure current user doc exists
    await firebaseChatService.ensureUserDocument();

    // Find existing conversation first
    const existing = await firebaseChatService.findConversationWithUser(recipientId);
    if (existing) {
      // Conversation exists, set it as current and navigate
      dispatch(setCurrentConversation(existing));
      return existing;
    }

    // Create temporary conversation
    const tempConversation = {
      id: `temp_${recipientId}`,
      participants: [recipientId],
      participantName: recipientName || 'User',
      conversationName: recipientName || 'User',
      participantId: recipientId,
      lastMessage: null,
      lastMessageTime: null,
      createdAt: Date.now(),
      isTemporary: true,
    };

    dispatch(setCurrentConversation(tempConversation));
    return tempConversation;
  } catch (error) {
    console.error('Error opening/creating conversation:', error);
    throw error;
  }
};

